import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { FaLinkedin, FaInstagram, FaTwitter, FaWhatsapp, FaGlobe, FaGithub, FaFacebook, FaTiktok, FaYoutube, FaSnapchat, FaPinterest, FaTelegram, FaSpotify, FaDiscord } from 'react-icons/fa';
import { SiThreads } from 'react-icons/si';
import { Download, Mail, Phone, Globe, ChevronRight, Share2, AlertTriangle, FileText, QrCode, X } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SOCIAL_ICONS = {
  LinkedIn: FaLinkedin,
  Instagram: FaInstagram,
  Twitter: FaTwitter,
  'X (Twitter)': FaTwitter,
  WhatsApp: FaWhatsapp,
  Website: FaGlobe,
  GitHub: FaGithub,
  Facebook: FaFacebook,
  TikTok: FaTiktok,
  YouTube: FaYoutube,
  Snapchat: FaSnapchat,
  Pinterest: FaPinterest,
  Telegram: FaTelegram,
  Spotify: FaSpotify,
  Discord: FaDiscord,
  Threads: SiThreads
};

const SOCIAL_COLORS = {
  LinkedIn: '#0A66C2',
  Instagram: '#E4405F',
  Twitter: '#1DA1F2',
  'X (Twitter)': '#000000',
  WhatsApp: '#25D366',
  Website: '#6B7280',
  GitHub: '#333',
  Facebook: '#1877F2',
  TikTok: '#000',
  YouTube: '#FF0000',
  Snapchat: '#FFFC00',
  Pinterest: '#E60023',
  Telegram: '#0088CC',
  Spotify: '#1DB954',
  Discord: '#5865F2',
  Threads: '#000000'
};

const PublicProfile = () => {
  const { username } = useParams();
  const [profile, setProfile] = useState(null);
  const [socialLinks, setSocialLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [leadForm, setLeadForm] = useState({ name: '', email: '', note: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [showQRModal, setShowQRModal] = useState(false);

  const profileUrl = `${window.location.origin}/p/${username}`;

  const fetchProfile = useCallback(async () => {
    try {
      const profileResponse = await axios.get(`${BACKEND_URL}/api/profiles/username/${username}`);
      setProfile(profileResponse.data);

      const linksResponse = await axios.get(`${BACKEND_URL}/api/profiles/${profileResponse.data.profile_id}/social-links`);
      setSocialLinks(linksResponse.data.filter(link => link.is_active));

      await axios.post(`${BACKEND_URL}/api/profiles/${profileResponse.data.profile_id}/analytics`);
    } catch (err) {
      console.error('Failed to fetch profile:', err);
      if (err.response?.status === 403) {
        setError({
          type: 'subscription_expired',
          message: err.response?.data?.detail || 'This profile is temporarily unavailable.'
        });
      } else if (err.response?.status === 404) {
        setError({
          type: 'not_found',
          message: 'Profile not found'
        });
      }
    } finally {
      setLoading(false);
    }
  }, [username]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleDownloadVCF = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/profiles/${profile.profile_id}/vcf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${username}.vcf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Contact saved!');
    } catch (error) {
      toast.error('Failed to download contact');
    }
  };

  const handleShare = async () => {
    await handleDownloadVCF();
  };

  const handleSubmitLead = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${BACKEND_URL}/api/profiles/${profile.profile_id}/leads`, leadForm);
      toast.success('Message sent successfully!');
      setLeadForm({ name: '', email: '', note: '' });
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-16 h-16 border-4 border-[#B8935A] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    if (error.type === 'subscription_expired') {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
          <div className="text-center max-w-md">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#B8935A]/15 flex items-center justify-center">
              <AlertTriangle className="w-10 h-10 text-[#B8935A]" />
            </div>
            <h1 className="text-3xl font-heading font-bold text-gray-900 mb-4">Profile Temporarily Unavailable</h1>
            <p className="text-gray-600 mb-6">{error.message}</p>
            <p className="text-sm text-gray-500 mb-8">
              The owner of this profile needs to renew their subscription to make it visible again.
            </p>
            <Link to="/">
              <Button className="bg-[#B8935A] hover:bg-[#a3824c] text-black rounded-full px-8">
                Visit Sol Digital
              </Button>
            </Link>
          </div>
        </div>
      );
    }
    
    return (
      <div className="min-h-screen flex items-center justify-center bg-white p-6">
        <div className="text-center">
          <h1 className="text-4xl font-heading font-bold text-gray-900 mb-4">Profile Not Found</h1>
          <p className="text-gray-600">This profile doesn't exist or has been removed.</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white p-6">
        <div className="text-center">
          <h1 className="text-4xl font-heading font-bold text-gray-900 mb-4">Profile Not Found</h1>
          <p className="text-gray-600">This profile doesn't exist or has been removed.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pb-12" data-testid="public-profile">
      <div className="max-w-md mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-[#B8935A] flex items-center justify-center">
              <span className="text-white font-bold text-xl">S</span>
            </div>
            <div className="flex flex-col">
              <span className="text-xl font-heading font-bold text-gray-900 leading-tight">SOL</span>
              <span className="text-xl font-heading font-bold text-gray-900 leading-tight">DIGITAL</span>
            </div>
          </div>
          <Button onClick={() => window.location.href = '/pricing'} variant="outline" className="rounded-full border-2 border-gray-900 text-gray-900 hover:bg-gray-100 font-semibold px-6 h-11" data-testid="get-card-button">
            Get Your Card
          </Button>
        </div>

        <div className="mb-6 rounded-3xl overflow-hidden bg-gray-900 relative" data-testid="profile-card">
          {/* QR Code Button - Top Right */}
          <button
            onClick={() => setShowQRModal(true)}
            className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30 flex items-center justify-center transition-all"
            data-testid="qr-code-button"
            aria-label="Show QR Code"
          >
            <QrCode className="w-5 h-5 text-white" />
          </button>

          <div className="flex items-stretch min-h-[280px]">
            <div className="w-2/5 relative overflow-hidden">
              <img 
                src={profile.profile_picture || 'https://images.unsplash.com/photo-1633332755192-727a05c4013d?w=400'} 
                alt={profile.name}
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="profile-picture"
              />
            </div>
            
            <div className="w-3/5 bg-gray-900 p-6 flex flex-col justify-end relative">
              <h1 className="text-3xl font-heading font-bold text-white mb-1 leading-tight" data-testid="profile-name">
                {profile.name}
              </h1>
              <p className="text-base text-gray-300" data-testid="profile-job-title">
                {profile.job_title || 'Professional'}
              </p>
              {profile.company && (
                <p className="text-sm text-gray-400 mt-1 mb-12" data-testid="profile-company">
                  {profile.company}
                </p>
              )}
              {profile.company_logo && (
                <img src={profile.company_logo} alt="Company Logo" className="absolute bottom-4 right-4 w-16 h-16 object-contain" data-testid="company-logo" />
              )}
            </div>
          </div>
        </div>

        <div className="mb-8 flex justify-center">
          <Button 
            onClick={handleDownloadVCF} 
            className="rounded-full bg-[#B8935A] hover:bg-[#a3824c] text-black font-semibold h-14 text-base shadow-lg px-12" 
            data-testid="download-contact-button"
          >
            Download Contact
          </Button>
        </div>

        {profile.bio && (
          <div className="mb-8" data-testid="about-section">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">About</h2>
            <p className="text-gray-700 leading-relaxed">{profile.bio}</p>
          </div>
        )}

        {socialLinks.length > 0 && (
          <div className="mb-8" data-testid="social-media-section">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Social media</h2>
            <div className="grid grid-cols-4 gap-4">
              {socialLinks.map((link) => {
                const Icon = SOCIAL_ICONS[link.platform] || FaGlobe;
                const color = SOCIAL_COLORS[link.platform] || '#6B7280';
                
                return (
                  <a
                    key={link.link_id}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="aspect-square rounded-2xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-all"
                    data-testid={`social-link-${link.platform.toLowerCase()}`}
                  >
                    <Icon className="w-7 h-7" style={{ color }} />
                  </a>
                );
              })}
            </div>
          </div>
        )}

        {profile.videos && profile.videos.length > 0 && (
          <div className="mb-8" data-testid="video-section">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Video</h2>
            {profile.videos.map((video, index) => (
              <div key={index} className="aspect-video rounded-3xl overflow-hidden mb-4 bg-black" data-testid={`video-${index}`}>
                <iframe src={video} title={`Video ${index + 1}`} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen></iframe>
              </div>
            ))}
          </div>
        )}

        {profile.gallery && profile.gallery.length > 0 && (
          <div className="mb-8" data-testid="gallery-section">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Gallery</h2>
            <div className="grid grid-cols-2 gap-3">
              {profile.gallery.map((photo, index) => (
                <img key={index} src={photo} alt={`Gallery ${index + 1}`} className="w-full aspect-square object-cover rounded-2xl" data-testid={`gallery-photo-${index}`} />
              ))}
            </div>
          </div>
        )}

        {profile.pdfs && profile.pdfs.length > 0 && (
          <div className="mb-8" data-testid="documents-section">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Documents</h2>
            <div className="space-y-3">
              {profile.pdfs.map((pdf, index) => (
                <a
                  key={index}
                  href={pdf.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 bg-gray-50 hover:bg-gray-100 rounded-xl p-4 transition-all group"
                  data-testid={`pdf-${index}`}
                >
                  <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-6 h-6 text-red-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-gray-900 font-medium">{pdf.title}</p>
                    <p className="text-sm text-gray-500 truncate">{pdf.filename}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors" />
                </a>
              ))}
            </div>
          </div>
        )}

        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact info.</h2>
          <div className="space-y-1">
            {profile.phone && (
              <a href={`tel:${profile.phone}`} className="flex items-center justify-between py-4 px-4 hover:bg-gray-50 rounded-xl transition-all group" data-testid="phone-link">
                <div className="flex items-center gap-4">
                  <Phone className="w-5 h-5 text-gray-600" />
                  <span className="text-gray-900 font-medium">{profile.phone}</span>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors" />
              </a>
            )}
            {profile.email && (
              <a href={`mailto:${profile.email}`} className="flex items-center justify-between py-4 px-4 hover:bg-gray-50 rounded-xl transition-all group" data-testid="email-link">
                <div className="flex items-center gap-4">
                  <Mail className="w-5 h-5 text-gray-600" />
                  <span className="text-gray-900 font-medium">{profile.email}</span>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors" />
              </a>
            )}
            {profile.website && (
              <a href={profile.website} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between py-4 px-4 hover:bg-gray-50 rounded-xl transition-all group" data-testid="website-link">
                <div className="flex items-center gap-4">
                  <Globe className="w-5 h-5 text-gray-600" />
                  <span className="text-gray-900 font-medium truncate">{profile.website}</span>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors" />
              </a>
            )}
          </div>
        </div>

        <div className="bg-gray-50 rounded-3xl p-6 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Get In Touch</h2>
          <form onSubmit={handleSubmitLead} className="space-y-4" data-testid="lead-capture-form">
            <div>
              <Label className="text-gray-900 mb-2 block font-medium">Your Name</Label>
              <Input value={leadForm.name} onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })} placeholder="John Doe" className="bg-white border-gray-200 text-gray-900 rounded-xl h-12" data-testid="lead-name-input" required />
            </div>
            <div>
              <Label className="text-gray-900 mb-2 block font-medium">Your Email</Label>
              <Input type="email" value={leadForm.email} onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })} placeholder="you@example.com" className="bg-white border-gray-200 text-gray-900 rounded-xl h-12" data-testid="lead-email-input" required />
            </div>
            <div>
              <Label className="text-gray-900 mb-2 block font-medium">Message</Label>
              <Textarea value={leadForm.note} onChange={(e) => setLeadForm({ ...leadForm, note: e.target.value })} placeholder="Let me know how I can help..." className="bg-white border-gray-200 text-gray-900 rounded-xl min-h-[100px]" data-testid="lead-note-input" />
            </div>
            <Button type="submit" disabled={submitting} className="w-full bg-[#B8935A] hover:bg-[#a3824c] text-black rounded-full font-semibold h-12 shadow-lg" data-testid="submit-lead-button">
              {submitting ? 'Sending...' : 'Send Message'}
            </Button>
          </form>
        </div>

        <div className="text-center mt-12">
          <p className="text-sm text-gray-500 mb-2">Powered by</p>
          <div className="flex items-center justify-center gap-2">
            <div className="w-6 h-6 rounded-full bg-[#B8935A] flex items-center justify-center">
              <span className="text-white font-bold text-sm">S</span>
            </div>
            <span className="text-xl font-heading font-bold text-gray-900">SOL DIGITAL</span>
          </div>
        </div>
      </div>

      {/* QR Code Modal */}
      {showQRModal && (
        <div 
          className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
          onClick={() => setShowQRModal(false)}
          data-testid="qr-modal-overlay"
        >
          <div 
            className="bg-white rounded-3xl p-8 max-w-sm w-full relative animate-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
            data-testid="qr-modal"
          >
            <button
              onClick={() => setShowQRModal(false)}
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
              data-testid="close-qr-modal"
            >
              <X className="w-4 h-4 text-gray-600" />
            </button>

            <div className="text-center">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Scan to Connect</h3>
              <p className="text-gray-500 text-sm mb-6">Share this QR code to let others view your digital card</p>
              
              <div className="bg-white p-4 rounded-2xl shadow-lg border border-gray-100 inline-block mb-6">
                <QRCodeSVG 
                  value={profileUrl} 
                  size={200}
                  level="H"
                  includeMargin={true}
                  data-testid="profile-qr-code"
                />
              </div>

              <div className="bg-gray-50 rounded-xl p-3 mb-4">
                <p className="text-xs text-gray-500 mb-1">Profile Link</p>
                <p className="text-sm text-gray-900 font-medium break-all">{profileUrl}</p>
              </div>

              <Button
                onClick={() => {
                  navigator.clipboard.writeText(profileUrl);
                  toast.success('Link copied to clipboard!');
                }}
                className="w-full bg-[#B8935A] hover:bg-[#a3824c] text-black rounded-full font-semibold h-11"
                data-testid="copy-link-button"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Copy Link
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicProfile;
